import React from "react";
import { Box } from "@mui/material";
import T from "../submissionTableTokens";
import { MeAvatar } from "./SubmissionsToolbar";
import { submissionTypeLong } from "./tableChips";
import {
  submissionBorrowerName,
  submissionEmail,
  submissionIsMine,
} from "../submissionHelpers";

const ellipsis = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/**
 * The first column's two-line identity cell, rendered inside
 * agGroupCellRenderer so the master/detail expander sits to its left.
 *
 * What it says depends on where the row is:
 *   ungrouped → borrower name over their email — the row IS a person's
 *               submission, and the person is what you scan for
 *   grouped   → the submission kind over "Submitted <date>", indented. The
 *               borrower is already named on the group header above, so
 *               repeating it on every child row buries the one thing that
 *               distinguishes them from each other.
 *
 * The monogram marks a submission that came through the signed-in user's own
 * link, so "mine" is visible without switching the filter on.
 */
const BorrowerCell = ({ data, currentUserId }) => {
  if (!data) return null;

  const inGroup = Boolean(data.__inGroup);
  const primary = inGroup
    ? submissionTypeLong(data)
    : submissionBorrowerName(data) || "—";
  const secondary = inGroup
    ? `Submitted ${data.__submittedLabel || "—"}`
    : submissionEmail(data);

  return (
    <Box
      sx={{
        lineHeight: 1.3,
        fontSize: 12,
        minWidth: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "7px", minWidth: 0 }}
      >
        <Box
          sx={{
            fontWeight: 600,
            color: T.ink900,
            fontSize: 12,
            lineHeight: 1.3,
            ...ellipsis,
          }}
        >
          {primary}
        </Box>
        {submissionIsMine(data, currentUserId) && (
          <MeAvatar name={data.__ownerName} active size={18} />
        )}
      </Box>
      {secondary && (
        <Box
          sx={{ fontSize: 11, lineHeight: 1.3, color: T.ink500, ...ellipsis }}
        >
          {secondary}
        </Box>
      )}
    </Box>
  );
};

export default BorrowerCell;
